/* The top of a member's record: who they are, and the one number that says whether
   anybody needs to do something about them.
 *
 * THE WHY IS SHOWN ONCE. The shipped demo prints it in the header AND in Fitness
 * Journey, which is the first of the ten polish items and the reason it is written
 * down. It lives in Overview, where the rest of their context is. */
import { cancellationOpen, type FirstNinety, type Member } from '@acrostic/model';
import { scoreOf, tone } from '../../components/People.js';
import { fmt } from '../../lib/fmt.js';
import { useSeesScores } from '../../data/SourceProvider.js';
import { seatLabel } from '../../seats.js';

/* The four states earn four tones, and `watch` is deliberately the same quiet one as
   `on track`: a single missed touch is a thing to notice, not a thing to alarm
   somebody about. An intervention is the only one that shouts. */
function trackTone(state: FirstNinety['state']): string {
  return state === 'intervention' ? 'risk' : state === 'overdue' ? 'warn' : 'good';
}

function stateLabel(state: FirstNinety['state']): string {
  return state === 'intervention' ? 'Formal intervention'
    : state === 'overdue' ? 'Touches overdue'
      : state === 'watch' ? 'Worth watching'
        : 'On track';
}

export function RecordHeader({ m, first90 }: { m: Member; first90?: FirstNinety | null }) {
  const seesScores = useSeesScores();
  const score = scoreOf(m);
  const sup = m.suppression;
  /* Open only. A resolved one is history and belongs on the timeline, not in the
     place the record says what is true about somebody today. */
  const cancelling = cancellationOpen(m) ? m.cancellation : null;

  return (
    <section className="panel record-head">
      <div className="face" aria-hidden="true">{fmt.initials(m.name)}</div>

      <div className="idblock">
        <h2 className="t-heading-h1">{m.name}</h2>
        <div className="t-body-small muted">
          {/* The plan used to lead this line. It is a commercial fact, not a way to
              reach somebody, and on a seat that may not see it the line was a lie of
              omission that started with a dash. */}
          {[m.email, m.phone].filter(Boolean).join(' · ') || 'No contact details on record'}
        </div>
        <div className="chips">
          <span className="chip t-label-small">{m.status}</span>
          {/* The seeded names already read "Coach Marcus J.", so prefixing the role
              gave "Coach Coach Marcus J.". The role is the quiet half and the person
              is the loud half, which is also the right way round for a product about
              who knows whom. */}
          {m.coach && (
            <span className="chip t-label-small"><span className="muted">Coach</span> {m.coach.replace(/^Coach /, '')}</span>
          )}
          {m.supportCoach && (
            <span className="chip t-label-small"><span className="muted">Support</span> {m.supportCoach.replace(/^Coach /, '')}</span>
          )}
          {m.tags.map((t) => <span className="chip t-label-small" key={t}>{t}</span>)}
        </div>
      </div>

      <div className="scoreblock">
        {/* CANCELLATION INITIATED OUTRANKS BOTH OF THESE (PRD 4). No score and no
            tier, on any seat: they are frequently Green when they ask, and the
            number is not the reason they are here. What they asked stands in its
            place, in the words it was heard in. */}
        {cancelling ? (
          <>
            <div className="t-heading-h2">Asked about cancelling</div>
            <div className="t-body-small">{cancelling.asked}</div>
            <div className="t-caption muted">
              {fmt.date(cancelling.askedAt)} · heard by {cancelling.recordedBy}
            </div>
            <div className="t-caption muted">
              The Owner speaks. Nothing automated reaches them.
            </div>
          </>
        ) : first90 ? (
        /* THE FIRST 90 DAYS, WHERE THE NUMBER WOULD BE (PRD 20). A new member has no
           baseline, so the model declines to score them and `isScored()` now means
           they genuinely hold no number rather than a hidden one. That leaves a hole
           exactly where every other record answers "does anybody need to do something
           about them", and the track is the answer that IS honest about them: which
           of the five owed touches have happened, who owes the next one, and whether
           that is fine, slipping or past saving by catching up.

           IT IS NOT A TAB AND NOT A SCREEN (Anuj's call, 18 September 2026). The
           programme has no area of its own; it is drawn here, on the record of the
           person it is about, and nowhere else. */
          <>
            <div className={'t-heading-h2 ' + trackTone(first90.state)}>
              Day {first90.day} of 90
            </div>
            <div className="t-caption muted">First 90 days · not scored yet</div>
            {/* The state and its sentence come from the model, so the record and any
                later surface cannot disagree about the same member. */}
            <div className="t-label-small step">{stateLabel(first90.state)}</div>
            <div className="t-body-small guidance">
              {first90.because}
              {first90.missedBy ? ` It was owed by the ${seatLabel(first90.missedBy)}.` : ''}
            </div>
            {first90.next && (
              <div className="t-caption muted">
                Next: {first90.next.label} · {first90.next.what}
                {first90.owedBy ? ` · ${seatLabel(first90.owedBy)}` : ''}
              </div>
            )}
            {first90.audit && (
              <div className="t-caption muted">
                Day-90 anchor audit: {first90.audit.anchors} named
                {first90.audit.passed ? ', which clears the bar of two.' : '. Two is the bar.'}
              </div>
            )}
          </>
        ) : sup ? (
        /* SITE-RULES 1. A suppressed member holds NO score: not a low one, not a
           stale one. Saying why, and when it is reviewed, is what stops suppression
           becoming abandonment. */
          <>
            <div className="t-heading-h2 muted">Not scored</div>
            <div className="t-caption muted">
              {sup.kind === 'hold' ? 'On hold' : 'On Care Track'}
              {sup.reason ? ` · ${sup.reason}` : ''}
            </div>
            <div className="t-caption muted">
              {sup.reviewAt ? `Review ${fmt.date(sup.reviewAt)}` : 'No review date set'}
            </div>
          </>
        ) : (
          <>
            {/* NO NUMBER AND NO TIER ON THE FLOOR (PRD 4). "Support Coach and Coach
                get the same worst-first order and the same reason in plain words,
                with no number and no tier dot. The ranking is the useful part; the
                number invites the floor to treat people as figures."

                The consoles have obeyed this all along - their rows are gated on
                `scored` - but the RECORD printed "36 Red" to every seat, which is
                the same rule broken one screen over. What a coach gets instead is
                what they can act on: the step and the move, which is the half of
                this panel that was ever any use to them. */}
            {seesScores ? (
              <>
                <div className={'bigscore t-display-small ' + tone(score)}>{score ?? '—'}</div>
                <div className="t-caption muted">{m.risk?.tier ?? 'Not scored'} · counts down from 100</div>
              </>
            ) : (
              <div className="t-caption muted">What to do next</div>
            )}
            {/* THE STEP IS ATTACHED TO THE ACTION, and this is the action (PRD 4).
                It sits ON the line that says what to do next, never beside the name
                in the header above, never as a column in a list, and never as
                anything sortable. The denominator is always printed: "Step 3 of 5"
                reads as a position in the gym's sequence, where a bare "3" next to
                a person reads as a level of that person - which is why the
                numbering was removed once before. */}
            {m.risk?.step && (
              <div className="t-label-small step">
                {m.risk.step.label} · {m.risk.step.name}
              </div>
            )}
            {m.risk?.guidance && <div className="t-body-small guidance">{m.risk.guidance}</div>}
            {m.risk?.step && (
              <div className="t-caption muted">{m.risk.step.because}</div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
